"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Container, Card, Button, Table, Form, Modal, Alert } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import {
  fetchAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from "../../../redux/slices/announcementsSlice"
import { fetchBuildings } from "../../../redux/slices/buildingsSlice"
import { compareBuildingNames } from "../../../data/buildings"
import { useTranslation } from "../../../hooks/use-translation"
import { Search, Edit, Trash2, Upload, X, ImageIcon } from "lucide-react"
import { PlusButton } from "../../../components/ui/plus-button"
import { useToast } from "../../../components/ui/use-toast"
import "../../../styles/admin-buttons.css"

export default function AdminAnnouncementsPage() {
  const dispatch = useAppDispatch()
  const { announcements = [], isLoading, error } = useAppSelector((state) => state.announcements)
  const { buildings = [] } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<any>(null)
  const [viewedAnnouncement, setViewedAnnouncement] = useState<any>(null)
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    buildings: [] as string[],
    sendEmail: false,
    enablePushNotification: false,
    showExpanded: false,
  })

  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [removeImage, setRemoveImage] = useState(false)

  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean
    announcementId: string
    announcementTitle: string
  }>({
    show: false,
    announcementId: "",
    announcementTitle: "",
  })

  useEffect(() => {
    dispatch(fetchAnnouncements())
    dispatch(fetchBuildings(false)) // Only fetch active buildings
  }, [dispatch])

  const resizeImage = (file: File, maxWidth: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) {
        reject(new Error("Please select a valid image file"))
        return
      }
      const img = new Image()
      img.onload = () => {
        if (img.width <= maxWidth) {
          resolve(file)
          return
        }
        const ratio = maxWidth / img.width
        const canvas = document.createElement("canvas")
        canvas.width = maxWidth
        canvas.height = Math.round(img.height * ratio)
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          reject(new Error("Failed to create canvas context"))
          return
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }))
            } else {
              reject(new Error("Failed to resize image"))
            }
          },
          "image/jpeg",
          0.9,
        )
      }
      img.onerror = () => reject(new Error("Failed to load image"))
      img.src = URL.createObjectURL(file)
    })
  }

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      try {
        const resized = await resizeImage(e.target.files[0], 1024)
        setImage(resized)
        setImagePreview(URL.createObjectURL(resized))
        setRemoveImage(false)
      } catch {
        toast({ title: "Error", description: t("invalidImageFile") || "Invalid image file", variant: "destructive" })
      }
    }
  }

  const handleRemoveImage = () => {
    setImage(null)
    setImagePreview(null)
    setRemoveImage(true)
  }

  const handleCreateAnnouncement = async () => {
    if (!formData.title || !formData.content || formData.buildings.length === 0) {
      toast({
        title: "Error",
        description: "Please fill in all fields and select at least one building",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        createAnnouncement({
          title: formData.title,
          content: formData.content,
          buildings: formData.buildings,
          sendEmail: formData.sendEmail,
          enablePushNotification: formData.enablePushNotification,
          showExpanded: formData.showExpanded,
          image: image || undefined,
        }),
      ).unwrap()

      if (result.success) {
        toast({
          title: "Success",
          description: `Announcement created successfully${
            formData.sendEmail && formData.enablePushNotification
              ? " with email and push notifications"
              : formData.sendEmail
                ? " with email notifications"
                : formData.enablePushNotification
                  ? " with push notifications"
                  : ""
          }`,
        })
        setShowModal(false)
        resetForm()
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create announcement",
        variant: "destructive",
      })
    }
  }

  const handleUpdateAnnouncement = async () => {
    if (!formData.title || !formData.content || formData.buildings.length === 0) {
      toast({
        title: "Error",
        description: "Please fill in all fields and select at least one building",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        updateAnnouncement({
          id: selectedAnnouncement.id,
          title: formData.title,
          content: formData.content,
          buildings: formData.buildings,
          sendEmail: formData.sendEmail,
          enablePushNotification: formData.enablePushNotification,
          showExpanded: formData.showExpanded,
          image: image || undefined,
          removeImage,
        }),
      ).unwrap()

      if (result.success) {
        toast({
          title: "Success",
          description: `Announcement updated successfully${
            formData.sendEmail && formData.enablePushNotification
              ? " with email and push notifications"
              : formData.sendEmail
                ? " with email notifications"
                : formData.enablePushNotification
                  ? " with push notifications"
                  : ""
          }`,
        })
        setShowModal(false)
        resetForm()
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update announcement",
        variant: "destructive",
      })
    }
  }

  const handleDeleteAnnouncement = async (id: string, title: string) => {
    setDeleteConfirm({
      show: true,
      announcementId: id,
      announcementTitle: title,
    })
  }

  const confirmDeleteAnnouncement = async () => {
    try {
      const result = await dispatch(deleteAnnouncement(deleteConfirm.announcementId)).unwrap()
      if (result.success) {
        toast({
          title: "Success",
          description: "Announcement deleted successfully",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete announcement",
        variant: "destructive",
      })
    } finally {
      setDeleteConfirm({ show: false, announcementId: "", announcementTitle: "" })
    }
  }

  const announcementBuildingNames = (announcement: any) =>
    announcement.buildings && announcement.buildings.includes("all")
      ? t("allBuildings")
      : announcement.buildings
          ?.map((buildingId: string) => {
            const building = buildings.find((b) => b.id === buildingId)
            return building ? building.name : buildingId
          })
          .sort(compareBuildingNames)
          .join(", ")

  const handleEditAnnouncement = (announcement: any) => {
    setSelectedAnnouncement(announcement)
    setFormData({
      title: announcement.title,
      content: announcement.content,
      buildings: announcement.buildings || [],
      sendEmail: false, // Default to false when editing
      enablePushNotification: false, // Default to false when editing
      showExpanded: announcement.showExpanded === true,
    })
    setImage(null)
    setImagePreview(announcement.imageUrl || null)
    setRemoveImage(false)
    setIsEditing(true)
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      buildings: [],
      sendEmail: false,
      enablePushNotification: false,
      showExpanded: false,
    })
    setImage(null)
    setImagePreview(null)
    setRemoveImage(false)
    setIsEditing(false)
    setSelectedAnnouncement(null)
  }

  const handleBuildingChange = (building: string) => {
    if (building === "all") {
      // Toggle "all" selection
      if (formData.buildings.includes("all")) {
        // If "all" is already selected, deselect it
        setFormData({
          ...formData,
          buildings: [],
        })
      } else {
        // If "all" is not selected, select only it
        setFormData({
          ...formData,
          buildings: ["all"],
        })
      }
      return
    }

    // If a specific building is selected, remove "all" if it exists
    const updatedBuildings = formData.buildings.filter((b) => b !== "all")

    if (updatedBuildings.includes(building)) {
      // If the building is already selected, deselect it
      setFormData({
        ...formData,
        buildings: updatedBuildings.filter((b) => b !== building),
      })
    } else {
      // If the building is not selected, select it
      setFormData({
        ...formData,
        buildings: [...updatedBuildings, building],
      })
    }
  }

  const filteredAnnouncements = announcements.filter(
    (announcement) =>
      announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.content.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  if (error) {
    return (
      <Container fluid className="py-4 px-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    )
  }

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("announcementManagement")}</h1>
        <PlusButton
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          title={t("newAnnouncement")}
        />
      </div>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="position-relative mb-3">
            <Form.Control
              type="text"
              placeholder={t("searchAnnouncements")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-5"
            />
            <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="fw-semibold text-gray-700">{t("title")}</th>
                    <th className="fw-semibold text-gray-700">{t("content")}</th>
                    <th className="fw-semibold text-gray-700">{t("buildings")}</th>
                    <th className="fw-semibold text-gray-700">{t("date")}</th>
                    <th className="fw-semibold text-gray-700">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAnnouncements.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        {t("noAnnouncementsFound")}
                      </td>
                    </tr>
                  ) : (
                    filteredAnnouncements.map((announcement) => (
                      <tr
                        key={announcement.id}
                        className="border-bottom"
                        onClick={() => setViewedAnnouncement(announcement)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="align-middle">
                          <div className="d-flex align-items-center gap-2">
                            {announcement.title}
                            {announcement.hasImage && <ImageIcon size={14} className="text-muted" />}
                          </div>
                        </td>
                        <td className="align-middle">
                          <div style={{ maxWidth: "300px", whiteSpace: "pre-line" }}>
                            {announcement.content.length > 200
                              ? `${announcement.content.substring(0, 200)}...`
                              : announcement.content}
                          </div>
                        </td>
                        <td className="align-middle">{announcementBuildingNames(announcement)}</td>
                        <td className="align-middle">{new Date(announcement.date).toLocaleDateString("el-GR")}</td>
                        <td className="align-middle">
                          <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              className="admin-edit-btn"
                              size="sm"
                              onClick={() => handleEditAnnouncement(announcement)}
                              title={t("editAnnouncement")}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              className="admin-delete-btn"
                              size="sm"
                              onClick={() => handleDeleteAnnouncement(announcement.id, announcement.title)}
                              title={t("deleteAnnouncement")}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Announcement Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? t("editAnnouncement") : t("newAnnouncement")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("title")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t("enterTitle")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("content")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder={t("enterContent")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("announcementImage") || "Image"}</Form.Label>
              {imagePreview ? (
                <div className="position-relative mb-2" style={{ maxWidth: "300px" }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "100%", borderRadius: "8px", objectFit: "cover" }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 m-1 rounded-circle p-1"
                    onClick={handleRemoveImage}
                    style={{ width: "28px", height: "28px" }}
                  >
                    <X size={14} />
                  </Button>
                </div>
              ) : null}
              <div>
                <label
                  htmlFor="announcement-image"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                  style={{ cursor: "pointer" }}
                >
                  <Upload size={16} />
                  {imagePreview ? t("changeImage") || "Change Image" : t("uploadImage") || "Upload Image"}
                </label>
                <input
                  id="announcement-image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("buildings")}</Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  id="building-all"
                  label={t("allBuildings")}
                  checked={formData.buildings.includes("all")}
                  onChange={() => handleBuildingChange("all")}
                  className="mb-2"
                />
                {buildings.map((building) => (
                  <Form.Check
                    key={building.id}
                    type="checkbox"
                    id={`building-${building.id}`}
                    label={building.name}
                    checked={formData.buildings.includes(building.id)}
                    onChange={() => handleBuildingChange(building.id)}
                    className="mb-2"
                    disabled={formData.buildings.includes("all")}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("notificationOptions")}</Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  id="send-email"
                  label={t("sendEmailNotification")}
                  checked={formData.sendEmail}
                  onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="enable-push"
                  label={t("enablePushNotification")}
                  checked={formData.enablePushNotification}
                  onChange={(e) => setFormData({ ...formData, enablePushNotification: e.target.checked })}
                  className="mb-2"
                />
                {formData.enablePushNotification && (
                  <Alert variant="info" className="mt-2">
                    <small>
                      {t("pushNotificationInfo") ||
                        "Push notifications will be sent to all users in the selected buildings."}
                    </small>
                  </Alert>
                )}
                {formData.sendEmail && (
                  <Alert variant="info" className="mt-2">
                    <small>{t("emailNotificationInfo")}</small>
                  </Alert>
                )}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="show-expanded"
                label={t("showExpandedOnHome")}
                checked={formData.showExpanded}
                onChange={(e) => setFormData({ ...formData, showExpanded: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button className="modal-cancel-btn" onClick={() => setShowModal(false)}>
            {t("cancel")}
          </button>
          <button
            className="modal-primary-btn"
            onClick={isEditing ? handleUpdateAnnouncement : handleCreateAnnouncement}
          >
            {isEditing ? t("update") : t("create")}
          </button>
        </Modal.Footer>
      </Modal>

      {/* View Announcement Modal (opened by clicking a row) */}
      <Modal show={!!viewedAnnouncement} onHide={() => setViewedAnnouncement(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{viewedAnnouncement?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewedAnnouncement && (
            <>
              <div className="text-muted small mb-3">
                {new Date(viewedAnnouncement.date).toLocaleDateString("el-GR")} ·{" "}
                {announcementBuildingNames(viewedAnnouncement)}
              </div>
              {viewedAnnouncement.imageUrl && (
                <img
                  src={viewedAnnouncement.imageUrl}
                  alt={viewedAnnouncement.title}
                  className="mb-3"
                  style={{ maxWidth: "100%", maxHeight: "400px", objectFit: "contain", borderRadius: "8px" }}
                />
              )}
              <div style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>{viewedAnnouncement.content}</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="modal-cancel-btn" onClick={() => setViewedAnnouncement(null)}>
            {t("close")}
          </button>
          <button
            className="modal-primary-btn"
            onClick={() => {
              const announcement = viewedAnnouncement
              setViewedAnnouncement(null)
              handleEditAnnouncement(announcement)
            }}
          >
            {t("editAnnouncement")}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirm.show}
        onHide={() => setDeleteConfirm({ show: false, announcementId: "", announcementTitle: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("confirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteAnnouncementConfirmation", { title: deleteConfirm.announcementTitle })}</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="modal-cancel-btn"
            onClick={() => setDeleteConfirm({ show: false, announcementId: "", announcementTitle: "" })}
          >
            {t("cancel")}
          </button>
          <button className="modal-delete-btn" onClick={confirmDeleteAnnouncement}>
            {t("delete")}
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
